/* ==========================================================================
   Two-factor login helpers (PORTAL-187): "remember this device" tokens

   After a successful authenticator code the backend may return a signed 30-day
   device token. Sending it with the next password login skips the code step on
   this browser. The server binds it to one account and to that account's
   current 2FA enrollment, so a stale or foreign token is simply ignored (the
   code is asked for again) — it can never unlock a different account.

   Keyed by portal kind + pharmacy slug + email: the same email can be a
   separate account at several pharmacies, so an email-only key would let one
   pharmacy's token overwrite another's and "remember" would never stick.
   Saving also writes an email-only copy for the global /admin/login page, which
   has no slug before sign-in. That copy is safe to share because the backend
   refuses slug-less login (409) for an email registered at more than one
   pharmacy, so it is only ever read for single-pharmacy emails.
   ========================================================================== */

import { safeGetItem, safeSetItem, safeRemoveItem } from './safeStorage'

export type TwoFactorPortal = 'provider' | 'admin' | 'internal'

const KEY_PREFIX: Record<TwoFactorPortal, string> = {
  provider: 'tjm_2fa_device',
  admin: 'tjm_admin_2fa_device',
  internal: 'tjm_internal_2fa_device',
}

function deviceKey(portal: TwoFactorPortal, email: string, slug?: string | null): string {
  const normalizedEmail = email.trim().toLowerCase()
  return slug
    ? `${KEY_PREFIX[portal]}:${slug.toLowerCase()}:${normalizedEmail}`
    : `${KEY_PREFIX[portal]}:${normalizedEmail}`
}

/** Read with the slug key when the portal is known, else the email-only copy. */
export function getTwoFactorDeviceToken(
  portal: TwoFactorPortal,
  email: string,
  slug?: string | null
): string | undefined {
  return safeGetItem(deviceKey(portal, email, slug)) ?? undefined
}

export function setTwoFactorDeviceToken(
  portal: TwoFactorPortal,
  email: string,
  token: string | null | undefined,
  slug?: string | null
): void {
  if (!token) return
  safeSetItem(deviceKey(portal, email), token)
  if (slug) {
    safeSetItem(deviceKey(portal, email, slug), token)
  }
}

export function clearTwoFactorDeviceToken(
  portal: TwoFactorPortal,
  email: string,
  slug?: string | null
): void {
  safeRemoveItem(deviceKey(portal, email))
  if (slug) {
    safeRemoveItem(deviceKey(portal, email, slug))
  }
}

/** Keep digits only (authenticator apps often show "123 456"). */
export function normalizeTwoFactorCode(value: string): string {
  return value.replace(/\D/g, '').slice(0, 6)
}
